
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiConfig } from "@/lib/env";

interface WebSocketConnectionProps {
  agentId?: string;
}

export const useWebSocketConnection = ({ agentId }: WebSocketConnectionProps = {}) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { toast } = useToast();
  const maxReconnectAttempts = 3;

  const connectWebSocket = useCallback(() => {
    try {
      // Use the provided agentId or fallback to a default
      const effectiveAgentId = agentId || "agent-001";
      const wsUrl = `${apiConfig.wsUrl}/agents/ws/${effectiveAgentId}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      
      // Close existing connection if available
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connection opened successfully');
        setIsConnected(true);
        setReconnectAttempts(0);
        
        // Don't send init message - just wait for user messages
        console.log('WebSocket ready for user messages');
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket connection closed with code:', event.code);
        setIsConnected(false);
        
        // Handle authentication failures
        if (event.code === 1008) {
          toast({
            title: "Authentication Failed",
            description: "Connection to chat server failed.",
            variant: "destructive",
          });
          setReconnectAttempts(maxReconnectAttempts);
        } else {
          setReconnectAttempts(prev => prev + 1);
          
          if (reconnectAttempts >= maxReconnectAttempts - 1) {
            toast({
              title: "Connection Failed",
              description: "Could not connect to chat server",
              variant: "destructive",
            });
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      setSocket(ws);
    } catch (error) {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
      setReconnectAttempts(prev => prev + 1);
    }
  }, [agentId, reconnectAttempts, socket, toast, maxReconnectAttempts]);

  useEffect(() => {
    if (!isConnected && reconnectAttempts < maxReconnectAttempts) {
      const timeout = setTimeout(() => {
        connectWebSocket();
      }, 2000 * Math.pow(1.5, reconnectAttempts));
      
      return () => clearTimeout(timeout);
    }
  }, [isConnected, reconnectAttempts, connectWebSocket, maxReconnectAttempts]);

  // Clean up socket on unmount or when agentId changes
  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [socket, agentId]);

  return {
    socket,
    isConnected,
    reconnectAttempts,
    maxReconnectAttempts,
    connectWebSocket
  };
};
